import React, { ReactElement } from 'react';
import {
    TSButtonLetterSpacingEnum,
    TSButtonSizeEnum,
    TSButtonVariantsEnum,
    TSButtonWeightEnum
} from 'enums/TSButtonVariants';
import { BillingIcon } from 'components/atoms/BillingIcon';
import { HomeIcon } from 'components/atoms/HomeIcon';
import { ContactIcon } from 'components/atoms/ContactIcon';
import { DocumentIcon } from 'components/atoms/DocumentIcon';
import { CancelIcon } from 'components/atoms/CancelIcon';
import { QuestionIcon } from 'components/atoms/QuestionIcon';
import {
    SCLinksButton,
    SCContactLink,
    SCLinksText,
    SCLinksWrapper,
    SCQuickLinksContentWrapper,
    SCQuickLinksTitle,
    SCQuickLinksTopBanner,
    SCQuickLinksWrapper
} from './styles';
import { ITSQuickLinksProps } from './types';
import { useHistory } from 'react-router-dom';

const TSQuickLinks = ({ id, ariaLabel, links, title, icon }: ITSQuickLinksProps): ReactElement => {
    const history = useHistory();
    const getIconForTitle = (titleText: string, iconName?: string): ReactElement => {
        if (iconName) {
            const key = iconName.toLowerCase();
            switch (key) {
                case 'billing':
                    return <BillingIcon iconState={false} disabled={false} />;
                case 'home':
                    return <HomeIcon iconState={false} disabled={false} />;
                case 'contact':
                    return <ContactIcon iconState={false} disabled={false} />;
                case 'document':
                    return <DocumentIcon iconState={false} disabled={false} />;
                case 'cancel':
                    return <CancelIcon iconState={false} disabled={false} />;
                case 'question':
                    return <QuestionIcon iconState={false} disabled={false} />;
                default:
                    break;
            }
        }
        const lower = titleText.toLowerCase();
        if (lower.includes('billing')) return <BillingIcon iconState={false} disabled={false} />;
        if (lower.includes('home')) return <HomeIcon iconState={false} disabled={false} />;
        if (lower.includes('contact')) return <ContactIcon iconState={false} disabled={false} />;
        if (lower.includes('document') || lower.includes('policy'))
            return <DocumentIcon iconState={false} disabled={false} />;
        if (lower.includes('cancel')) return <CancelIcon iconState={false} disabled={false} />;
        return <QuestionIcon iconState={false} disabled={false} />;
    };
    const handleLinkClick = (destination: string, arrivalOption: string): void => {
        switch (arrivalOption) {
            case 'newWindow': {
                window.open(destination);
                break;
            }
            case 'route': {
                history.push(destination);
                break;
            }
            case 'scroll': {
                const element = document.getElementById(destination);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
                break;
            }
            default:
                break;
        }
    };

    return (
        <SCQuickLinksWrapper id={id} aria-label={ariaLabel}>
            <SCQuickLinksTopBanner />
            <SCQuickLinksContentWrapper>
                <SCQuickLinksTitle>{title}</SCQuickLinksTitle>
                <SCLinksWrapper>
                    {links.map((link, index) => (
                        <SCLinksText
                            key={`Key${index}`}
                            data-testid={link.arrivalOption === 'scroll' && 'scroll'}
                            isLinkActive={link.isLinkActive}
                        >
                            {link.contactLink ? (
                                <SCContactLink href={link.contactLink} aria-label={link.title}>
                                    {link.title}
                                </SCContactLink>
                            ) : (
                                <SCLinksButton
                                    ariaLabel={link.title}
                                    size={TSButtonSizeEnum.MEDIUM}
                                    letterSpacing={TSButtonLetterSpacingEnum.NARROW}
                                    weight={TSButtonWeightEnum.BOLD}
                                    tsVariant={
                                        link.iconName ? TSButtonVariantsEnum.ICON : TSButtonVariantsEnum.UNDERLINE
                                    }
                                    icon={link.iconName ? getIconForTitle(link.iconName) : undefined}
                                    onClick={
                                        link.onClick
                                            ? link.onClick
                                            : (event): void => {
                                                  event.preventDefault();
                                                  handleLinkClick(link.destination, link.arrivalOption);
                                              }
                                    }
                                >
                                    {link.title}
                                </SCLinksButton>
                            )}
                        </SCLinksText>
                    ))}
                </SCLinksWrapper>
            </SCQuickLinksContentWrapper>
        </SCQuickLinksWrapper>
    );
};

export { TSQuickLinks };
